import * as React from "react";

// NPM
import clsx from "clsx";
import { animated } from "react-spring";
import moment from "moment";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { FormControlLabel, Grid, GridProps, Switch } from "@material-ui/core";
import { ParsableDate } from "@material-ui/pickers/constants/prop-types";

// Utility
import Utility from "../../domain/classes/Utility";

// Components
import StyledPromptedTextField from "../../components/common/PromptedTextField.Component";
import FormattedFieldSet from "../common/FormattedFieldSet.Component";
import StyledPromptedDatePicker from "../common/PromptedDatePIcker.Component";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";

// Classes
import DateTime from "../../domain/classes/DateTime";

export const styles = (theme: Theme) => createStyles({ root: {} });

export interface StudentStatusFieldsContainerProps extends GridProps {}

const StudentStatusFieldsContainer = React.forwardRef<
	HTMLDivElement,
	StudentStatusFieldsContainerProps & WithStyles<typeof styles>
>(({ classes, className, ...gridProps }, ref) => {
	// Contexts
	const {
		patientDetails,
		handlePatientDetailsChange,
	} = useApplicationManager();

	// States
	const [
		studentNumberExpiry,
		setStudentNumberExpiry,
	] = React.useState<ParsableDate>(
		moment(patientDetails.studentNumberExpiry).toDate()
	);
	const [isStudent, setIsStudent] = React.useState<boolean>(
		patientDetails.isStudent
	);

	// Handlers
	const handleStudentNumberExpiryChange = (date: any) => {
		setStudentNumberExpiry(moment(date).toDate());
	};

	const toggleStudentStatus = () => {
		setIsStudent((prevState: boolean) => !prevState);
	};

	// Effects
	React.useEffect(() => {
		handlePatientDetailsChange("isStudent", isStudent);
		if (!isStudent) {
			handlePatientDetailsChange("studentNumber", "");
			handlePatientDetailsChange("studentNumberExpiry", "");
			handlePatientDetailsChange("institutionName", "");
		}
	}, [isStudent, handlePatientDetailsChange]);

	React.useEffect(() => {
		handlePatientDetailsChange(
			"studentNumberExpiry",
			moment(studentNumberExpiry).format(DateTime.DEFAULT_DATE_FORMAT)
		);
	}, [studentNumberExpiry, handlePatientDetailsChange]);

	// Handlers
	const handleInstitutionsNameChange = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		handlePatientDetailsChange("institutionName", event.target.value);
	};

	const handleStudentNumberChange = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		handlePatientDetailsChange("studentNumber", event.target.value);
	};

	return (
		<Grid
			container
			spacing={1}
			ref={ref}
			{...gridProps}
			className={clsx(classes.root, className)}
		>
			<Grid item xs={12}>
				<FormattedFieldSet label="Student status">
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<FormControlLabel
								control={
									<Switch
										checked={patientDetails.isStudent}
										onChange={toggleStudentStatus}
										name="Student status"
										color="primary"
									/>
								}
								label={
									patientDetails.isPersonBooking
										? "Are you a student?"
										: "Is the patient a student?"
								}
							/>
						</Grid>
						{isStudent && (
							<React.Fragment>
								<Grid item xs={12} sm={6} md={4}>
									<StyledPromptedTextField
										title={`The name of the school/college/univerity/TAFE that ${
											patientDetails.isPersonBooking
												? "you"
												: "the patient"
										} attend${
											patientDetails.isPersonBooking
												? ""
												: "s"
										}`}
										fullWidth
										variant="outlined"
										label="Institution's name"
										size="small"
										onBlur={handleInstitutionsNameChange}
										defaultValue={
											patientDetails.institutionName
										}
									/>
								</Grid>
								<Grid item xs={6} md={4}>
									<StyledPromptedTextField
										title={`${
											patientDetails.isPersonBooking
												? "Your"
												: "The patient's"
										} student number`}
										fullWidth
										variant="outlined"
										label="Student number"
										size="small"
										onBlur={handleStudentNumberChange}
										defaultValue={
											patientDetails.studentNumber
										}
									/>
								</Grid>
								<Grid item xs={6} md={4}>
									<StyledPromptedDatePicker
										value={studentNumberExpiry}
										onChange={
											handleStudentNumberExpiryChange
										}
										title={`Expiry on ${
											patientDetails.isPersonBooking
												? "your"
												: "the patient's"
										} student card`}
										label="Expiry"
										disablePast
									/>
								</Grid>
							</React.Fragment>
						)}
					</Grid>
				</FormattedFieldSet>
			</Grid>
		</Grid>
	);
});

const StyledStudentStatusFieldsContainer = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("StudentStatusFieldsContainer"),
})(StudentStatusFieldsContainer);

export default StyledStudentStatusFieldsContainer;

export const AnimatedStudentStatusFieldsContainer = animated(
	StyledStudentStatusFieldsContainer
);
